/**
 * メッセージスレッド管理フック
 * 企業・学生共通のスレッド操作を提供
 */

import { useState, useCallback, useEffect } from 'react';
import {
  MessageThread,
  ThreadDetail,
  ThreadMessage,
  ThreadListQuery,
  SendMessageRequest,
  UpdateThreadStatusRequest,
  ThreadStatus,
  ThreadUIState,
  SenderType,
} from '../types/message-thread';
import { messageThreadService } from '../services/messageThreadService';
import { useToast } from './useToast';
import { useAuth } from './useAuth';

export type UserType = 'company' | 'student';

interface UseMessageThreadsOptions {
  userType: UserType;
  companyId?: number; // 企業側の場合は必須
  autoLoad?: boolean;
  initialQuery?: ThreadListQuery;
}

// APIレスポンスの企業データ型定義
export interface CompanyData {
  companyName?: string;
  name?: string;
  logoUrl?: string;
  logo_url?: string;
  [key: string]: any;
}

// APIレスポンスの学生データ型定義
export interface StudentData {
  fullName?: string;
  name?: string;
  avatarUrl?: string;
  avatar_url?: string;
  [key: string]: any;
}

// 企業情報の正規化関数
export const normalizeCompany = (company?: CompanyData) => {
  if (!company) return undefined;
  const logoUrl = company.logoUrl || company.logo_url || company.logoUrl;
  return {
    ...company,
    name: company.companyName || company.name, // companyNameをnameにマッピング
    logoUrl,
  };
};

// 学生情報の正規化関数
export const normalizeStudent = (student?: StudentData) => {
  if (!student) return undefined;
  const avatarUrl = student.avatarUrl || student.avatar_url || student.avatarUrl;
  return {
    ...student,
    name: student.fullName || student.name, // fullNameをnameにマッピング
    avatarUrl,
  };
};

export const useMessageThreads = ({
  userType,
  companyId,
  autoLoad = true,
  initialQuery = {},
}: UseMessageThreadsOptions) => {
  const { showToast } = useToast();
  const { user } = useAuth();

  // State
  const [threads, setThreads] = useState<MessageThread[]>([]);
  const [currentThread, setCurrentThread] = useState<ThreadDetail | null>(null);
  const [uiState, setUIState] = useState<ThreadUIState>({
    selectedThreadId: null,
    isLoading: false,
    isLoadingDetail: false,
    isSending: false,
    error: null,
    filters: {},
    sortBy: 'lastMessageAt',
    sortOrder: 'DESC',
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1,
  });

  // スレッド一覧取得
  const loadThreads = useCallback(
    async (query: ThreadListQuery = {}) => {
      try {
        setUIState((prev) => ({ ...prev, isLoading: true, error: null }));

        const mergedQuery = { ...initialQuery, ...query };
        let response;

        if (userType === 'company') {
          if (!companyId) {
            throw new Error('Company ID is required for company user type');
          }
          response = await messageThreadService.getCompanyThreads(
            companyId,
            mergedQuery
          );
        } else {
          response = await messageThreadService.getStudentThreads(mergedQuery);
        }

        // スレッドの企業名・学生名を正規化
        const normalizedThreads = response.data.threads.map((thread) => ({
          ...thread,
          company: normalizeCompany(thread.company),
          student: normalizeStudent(thread.student),
        }));

        setThreads(normalizedThreads);
        setPagination(response.data.pagination);
      } catch (error: any) {
        console.error('Failed to load threads:', error);
        const errorMessage =
          error?.response?.data?.message || 'スレッド一覧の取得に失敗しました';
        setUIState((prev) => ({ ...prev, error: errorMessage }));
        showToast(errorMessage, 'error');
      } finally {
        setUIState((prev) => ({ ...prev, isLoading: false }));
      }
    },
    [userType, companyId, initialQuery, showToast]
  );

  // スレッド詳細取得
  const loadThreadDetail = useCallback(
    async (threadId: string) => {
      try {
        setUIState((prev) => ({
          ...prev,
          isLoadingDetail: true,
          selectedThreadId: threadId,
          error: null,
        }));

        let response;

        if (userType === 'company') {
          if (!companyId) {
            throw new Error('Company ID is required for company user type');
          }
          response = await messageThreadService.getCompanyThreadDetail(
            companyId,
            threadId
          );
        } else {
          response =
            await messageThreadService.getStudentThreadDetail(threadId);
        }

        const getSenderInfo = (message: ThreadMessage): {
          name: string;
          avatarUrl?: string;
        } => {
          if (message.senderType === SenderType.COMPANY) {
            const company = normalizeCompany(response.data.thread.company);
            if (company && company.name) {
              return {
                name: company.name,
                avatarUrl: message.sender?.avatarUrl || company.logoUrl,
              };
            }
            // フォールバック: senderのfullNameを使用（従業員名）
            return {
              name: message.sender?.fullName || '企業',
              avatarUrl: message.sender?.avatarUrl,
            };
          }

          if (message.senderType === SenderType.STUDENT) {
            const student = normalizeStudent(response.data.thread.student);
            if (student && student.name) {
              return {
                name: student.name,
                avatarUrl: message.sender?.avatarUrl || student.avatarUrl,
              };
            }
            // フォールバック: senderのfullNameを使用（学生名）
            return {
              name: message.sender?.fullName || '学生',
              avatarUrl: message.sender?.avatarUrl,
            };
          }

          // その他の場合（フォールバック）
          return {
            name: message.sender?.fullName || message.sender?.name || '送信者不明',
            avatarUrl: message.sender?.avatarUrl,
          };
        };

        const enrichedCompany = normalizeCompany(response.data.thread.company);
        const enrichedStudent = normalizeStudent(response.data.thread.student);

        const enrichedDetail: ThreadDetail = {
          ...response.data,
          thread: {
            ...response.data.thread,
            company: enrichedCompany,
            student: enrichedStudent,
          },
          messages: response.data.messages.map((message) => ({
            ...message,
            sender: getSenderInfo(message),
          })),
        };

        setCurrentThread(enrichedDetail);

        // ブロック状態でない場合のみ未読カウントをリセット
        const shouldMarkAsRead = !(
          userType === 'student' && enrichedDetail.thread.studentBlocked
        );

        // スレッド一覧でもアバター・ロゴを補完
        setThreads((prev) =>
          prev.map((thread) =>
            thread.id === threadId
              ? {
                  ...thread,
                  company: enrichedCompany
                    ? {
                        ...thread.company,
                        ...enrichedCompany,
                        logoUrl:
                          enrichedCompany.logoUrl || thread.company?.logoUrl,
                      }
                    : thread.company,
                  student: enrichedStudent
                    ? {
                        ...thread.student,
                        ...enrichedStudent,
                        avatarUrl:
                          enrichedStudent.avatarUrl ||
                          thread.student?.avatarUrl,
                      }
                    : thread.student,
                  unreadCount: shouldMarkAsRead ? 0 : thread.unreadCount,
                }
              : thread
          )
        );

        if (shouldMarkAsRead) {
          await markAsRead(threadId);
        }
      } catch (error: any) {
        console.error('Failed to load thread detail:', error);
        const errorMessage =
          error?.response?.data?.message || 'スレッド詳細の取得に失敗しました';
        setUIState((prev) => ({ ...prev, error: errorMessage }));
        showToast(errorMessage, 'error');
      } finally {
        setUIState((prev) => ({ ...prev, isLoadingDetail: false }));
      }
    },
    [userType, companyId, showToast]
  );

  // メッセージ送信
  const sendMessage = useCallback(
    async (threadId: string, messageData: SendMessageRequest) => {
      try {
        setUIState((prev) => ({ ...prev, isSending: true, error: null }));

        let response;

        if (userType === 'company') {
          if (!companyId) {
            throw new Error('Company ID is required for company user type');
          }
          response = await messageThreadService.sendCompanyReply(
            companyId,
            threadId,
            messageData
          );
        } else {
          response = await messageThreadService.sendStudentReply(
            threadId,
            messageData
          );
        }

        // スレッド詳細を再読み込み
        await loadThreadDetail(threadId);

        // スレッド一覧を更新
        setThreads((prev) =>
          prev.map((thread) =>
            thread.id === threadId
              ? {
                  ...thread,
                  messageCount: thread.messageCount + 1,
                  lastMessageAt: response.data.sentAt,
                  latestMessage: {
                    preview: messageData.messageBody.substring(0, 50) + '...',
                    sentAt: response.data.sentAt,
                    senderType:
                      userType === 'company'
                        ? SenderType.COMPANY
                        : SenderType.STUDENT,
                  },
                }
              : thread
          )
        );

        showToast('メッセージを送信しました', 'success');
        return response.data;
      } catch (error: any) {
        console.error('Failed to send message:', error);
        const errorMessage =
          error?.response?.data?.message || 'メッセージの送信に失敗しました';
        setUIState((prev) => ({ ...prev, error: errorMessage }));
        showToast(errorMessage, 'error');
        throw error;
      } finally {
        setUIState((prev) => ({ ...prev, isSending: false }));
      }
    },
    [userType, companyId, loadThreadDetail, showToast]
  );

  // スレッド状態更新
  const updateThreadStatus = useCallback(
    async (threadId: string, status: ThreadStatus) => {
      try {
        const statusData: UpdateThreadStatusRequest = { status };

        if (userType === 'company') {
          if (!companyId) {
            throw new Error('Company ID is required for company user type');
          }
          await messageThreadService.updateCompanyThreadStatus(
            companyId,
            threadId,
            statusData
          );
        } else {
          await messageThreadService.updateStudentThreadStatus(
            threadId,
            statusData
          );
        }

        // スレッド一覧を更新
        setThreads((prev) =>
          prev.map((thread) =>
            thread.id === threadId ? { ...thread, status } : thread
          )
        );

        // 現在のスレッドも更新
        if (currentThread?.thread.id === threadId) {
          setCurrentThread((prev) =>
            prev ? { ...prev, thread: { ...prev.thread, status } } : null
          );
        }

        const statusText = {
          [ThreadStatus.ACTIVE]: 'アクティブ',
          [ThreadStatus.ARCHIVED]: 'アーカイブ',
          [ThreadStatus.BLOCKED]: 'ブロック',
          [ThreadStatus.CLOSED]: 'クローズ',
        }[status];

        showToast(`スレッドを${statusText}に変更しました`, 'success');
      } catch (error: any) {
        console.error('Failed to update thread status:', error);
        const errorMessage =
          error?.response?.data?.message || 'スレッド状態の更新に失敗しました';
        showToast(errorMessage, 'error');
        throw error;
      }
    },
    [userType, companyId, currentThread, showToast]
  );

  // 既読処理
  const markAsRead = useCallback(
    async (threadId: string) => {
      try {
        // ブロック状態の確認
        const thread = threads.find((t) => t.id === threadId);
        if (thread && userType === 'student' && thread.studentBlocked) {
          // 学生がブロックしている場合は既読にしない
          return;
        }

        if (userType === 'company') {
          if (!companyId) return;
          await messageThreadService.markCompanyMessagesAsRead(
            companyId,
            threadId
          );
        } else {
          await messageThreadService.markStudentMessagesAsRead(threadId);
        }
      } catch (error: any) {
        console.error('Failed to mark as read:', error);
        // 既読処理のエラーは表示しない（UX向上のため）
      }
    },
    [userType, companyId, threads]
  );

  // フィルター・ソート更新
  const updateFilters = useCallback(
    (newFilters: Partial<ThreadUIState['filters']>) => {
      const updatedFilters = { ...uiState.filters, ...newFilters };
      setUIState((prev) => ({
        ...prev,
        filters: updatedFilters,
      }));

      // フィルター更新時に検索を実行
      loadThreads(updatedFilters);
    },
    [loadThreads, uiState.filters]
  );

  const updateSort = useCallback(
    (
      sortBy: ThreadUIState['sortBy'],
      sortOrder: ThreadUIState['sortOrder']
    ) => {
      setUIState((prev) => ({ ...prev, sortBy, sortOrder }));
    },
    []
  );

  // 初期データ読み込み
  useEffect(() => {
    if (
      autoLoad &&
      (userType === 'student' || (userType === 'company' && companyId))
    ) {
      loadThreads();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLoad, companyId, userType]);

  // ページネーション
  const loadNextPage = useCallback(() => {
    if (pagination.page < pagination.totalPages) {
      loadThreads({ page: pagination.page + 1 });
    }
  }, [pagination, loadThreads]);

  const loadPrevPage = useCallback(() => {
    if (pagination.page > 1) {
      loadThreads({ page: pagination.page - 1 });
    }
  }, [pagination, loadThreads]);

  return {
    // State
    threads,
    currentThread,
    uiState,
    pagination,

    // Actions
    loadThreads,
    loadThreadDetail,
    sendMessage,
    updateThreadStatus,
    markAsRead,

    // UI State Management
    updateFilters,
    updateSort,
    setCurrentThread,

    // Pagination
    loadNextPage,
    loadPrevPage,

    // Utilities
    hasNextPage: pagination.page < pagination.totalPages,
    hasPrevPage: pagination.page > 1,
    totalUnread: threads.reduce(
      (sum, thread) => sum + (thread.unreadCount || 0),
      0
    ),
  };
};
